import React, { FC, memo, useCallback, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { useButton, useCurrency, useI18n, useLogger } from '~Hooks';
import Icon from '~ComponentsImproved/Icon';
import { useExpressCartActions } from '../../../Providers/ExpressCartProvider';
import { ExpressCartSuggestion } from '../../../Providers/ExpressProductLibraryProvider';
import ItemImage from '../../../Components/ItemImage';
import plusIcon from '~Scenes/SelfServiceKiosks/Assets/Icons/plus.svg';
import checkIcon from '~Assets/icons/check.svg';

/** Widest the card gets - the CDN crop size. Its rendered width is --eposSuggestionCardWidth. */
const CARD_WIDTH = 200;

/** How long the "Added" confirmation stays on the photo. */
const ADDED_FLASH_MS = 900;

/**
 * Compact photo card: one tap attaches the add-on to its parent line. The
 * card stays - another tap adds another - so the tap is confirmed with a
 * short "Added" over the photo instead. Fades in on mount.
 */
const Card = styled.div`
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 6px;
  border-radius: 13px;
  cursor: pointer;
  user-select: none;
  border: 2px solid var(--eposBorderColor);
  color: var(--eposText);

  &.is-locked {
    cursor: default;
  }
  transition: transform 0.15s ease-out, border-color 0.15s ease-out;
  animation: eposFadeIn 0.3s ease-out both;

  &.is-touching {
    transform: scale(0.96);
    border-color: var(--eposButton);
  }

  @keyframes eposFadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

/** Wraps the photo; carries the `is-added` class so React never re-classes it. */
const Photo = styled.div`
  position: relative;
`;

/** Drops over the photo for a moment after a tap. Class toggled via ref, no render. */
const AddedFlash = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 800;
  color: var(--eposButtonText);
  background: var(--eposButton);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease-out;

  .is-added & {
    opacity: 0.92;
  }
`;

const Title = styled.div`
  font-size: 12px;
  font-weight: 700;
  line-height: 1.2;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

/** Delta price left, plus glyph right - the same "hint, not alert" outline as the menu card badge. */
const Foot = styled.div`
  margin-top: auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 10px;
  font-weight: 700;
  opacity: 0.7;
`;

interface Props {
  suggestion: ExpressCartSuggestion;
}

const SuggestionCard: FC<Props> = ({ suggestion }) => {
  const currency = useCurrency();
  const { i18n } = useI18n();
  const logger = useLogger('epos-suggestion-card');
  const cartActions = useExpressCartActions();

  const photoRef = useRef<HTMLDivElement>(null);
  const flashTimerRef = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(flashTimerRef.current), []);

  const flashAdded = useCallback(() => {
    const node = photoRef.current;
    if (!node) {
      return;
    }
    node.classList.add('is-added');
    window.clearTimeout(flashTimerRef.current);
    flashTimerRef.current = window.setTimeout(
      () => node.classList.remove('is-added'),
      ADDED_FLASH_MS,
    );
  }, []);

  /** Always attached to the parent line - a suggestion is an upsell, never a loose item. */
  const handleTap = useCallback(() => {
    logger.info('Suggestion tapped in cart', {
      itemId: suggestion.item.id,
      parentCartId: suggestion.parentCartId,
    });
    cartActions.addUpsell(suggestion.item, suggestion.parentCartId);
    flashAdded();
  }, [suggestion, cartActions, flashAdded]);

  const { touching, disabled, disabledToast, ...button } = useButton({ onClick: handleTap });

  const delta =
    suggestion.item.priceValue > 0
      ? `+ ${currency.format(suggestion.item.priceValue)}`
      : suggestion.item.price;

  return (
    <Card
      {...button}
      role="button"
      className={touching ? 'is-touching' : undefined}
      data-testid="epos-suggestion"
      data-item-id={suggestion.item.id}
      data-parent-id={suggestion.parentCartId}>
      <Photo ref={photoRef}>
        <ItemImage imageUrl={suggestion.item.imageUrl} cdnWidth={CARD_WIDTH} borderRadius={12} />
        <AddedFlash>
          <Icon src={checkIcon} color="var(--eposButtonText)" width="15px" height="15px" />
          {i18n('Express.Item.Added')}
        </AddedFlash>
      </Photo>
      <Title>{suggestion.item.title}</Title>
      <Foot>
        <span>{delta}</span>
        <Icon src={plusIcon} color="var(--eposText)" width="10px" height="10px" />
      </Foot>
    </Card>
  );
};

export default memo(SuggestionCard);
